import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { formatDate, formatPrice, SUPPORTED_CURRENCIES, currencyForCountry } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Pencil, Handshake, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const AGREEMENT_LABEL = {
    percentage_split: "Percentage Split",
    flat_rental: "Flat Venue Rental",
    artist_guarantee: "Artist Guarantee",
    guarantee_plus_percentage: "Guarantee + Percentage",
    no_agreement: "No Financial Agreement",
};

const STATUS_STYLE = {
    settled: "border-[#00e676] text-[#00e676]",
    ready_to_settle: "border-[#00e676] text-[#00e676] bg-[#00e676]/5",
    awaiting_revenue: "border-[#ffea00] text-[#ffea00]",
    upcoming: "border-[#a3a3a3] text-[#a3a3a3]",
    no_settlement_required: "border-[#666] text-[#666]",
};

// All Agreement Type + Settlement Status pills share this shell so they
// render as ONE coherent badge — no stair-step border wrap when the label
// wraps at narrow widths.
const BADGE_CLS = "inline-flex items-center whitespace-nowrap font-mono uppercase text-[10px] leading-none px-2 py-1 border";

const STATUS_LABEL = {
    settled: "SETTLED",
    ready_to_settle: "READY TO SETTLE",
    awaiting_revenue: "AWAITING REVENUE",
    upcoming: "UPCOMING",
    no_settlement_required: "NO SETTLEMENT REQUIRED",
};

// Consistent Project Mint currency convention: `ISO 0,000.00`.
// NOTE: never invent a currency here — if the caller has no resolved
// currency for the event/agreement, we render the amount alone so the
// business surface never claims a value is in USD when it isn't.
function money(v, currency) {
    if (v == null || v === "") return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    const amt = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${currency} ${amt}` : amt;
}

// Best-guess currency for a settlement row, using the same priority the
// AgreementDialog uses: stored agreement.currency → event.currency →
// venue country. Never falls back to USD.
function rowCurrency(r) {
    return r?.agreement_currency
        || r?.event_currency
        || currencyForCountry(r?.venue_country)
        || "";
}

export default function Business() {
    const { activeArtistId } = useRole();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [busySettleId, setBusySettleId] = useState(null);

    const load = () => {
        if (!activeArtistId) return;
        setLoading(true);
        endpoints.business(activeArtistId).then((d) => {
            setRows(d.settlements);
            setLoading(false);
        }).catch(() => setLoading(false));
    };
    useEffect(load, [activeArtistId]);

    const doSettle = async (r) => {
        const rc = rowCurrency(r);
        const totals = [];
        const codePrefix = rc ? `${rc} ` : "";
        if (r.artist_share != null) totals.push(`Artist ${codePrefix}${Number(r.artist_share).toFixed(2)}`);
        if (r.venue_share != null) totals.push(`Venue ${codePrefix}${Number(r.venue_share).toFixed(2)}`);
        const summary = totals.length ? ` (${totals.join(" · ")})` : "";
        if (!window.confirm(`Mark "${r.event_name}" as settled${summary}? Project Mint stores the record — it does not move any money.`)) return;
        setBusySettleId(r.event_id);
        try {
            await endpoints.markEventSettled(r.event_id);
            toast.success("Settlement recorded");
            load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusySettleId(null); }
    };

    const doReopen = async (r) => {
        if (!window.confirm(`Reopen the settlement for "${r.event_name}"? This clears the settled status and lets you edit revenue again.`)) return;
        setBusySettleId(r.event_id);
        try {
            await endpoints.reopenEventSettlement(r.event_id);
            toast.success("Settlement reopened");
            load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusySettleId(null); }
    };

    return (
        <div className="pm-fade" data-testid="business-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Business</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Venue agreements & settlements</h1>
                <p className="mt-2 text-xs font-mono uppercase text-[#666]">Organization only — no transactions processed on Project Mint.</p>
                <p className="mt-1 text-[10px] font-mono uppercase text-[#666] max-w-2xl">
                    Each event's terms are stored on the event itself — Project Mint never assumes a default split.
                    Attach an agreement below, enter actual revenue after the event, and use this as your organizational record.
                </p>
            </header>

            {loading ? (
                <div className="p-6 md:p-10 font-mono text-sm text-[#666]" data-testid="business-loading">Loading…</div>
            ) : rows.length === 0 ? (
                <EmptyState />
            ) : (
                <div className="px-4 md:px-8 py-6">
                    <div className="border border-[#2a2a2a] overflow-x-auto">
                        <table className="w-full text-sm min-w-[1100px]">
                            <thead>
                                <tr className="border-b border-[#2a2a2a]">
                                    <Th>Event</Th>
                                    <Th>Date</Th>
                                    <Th>Venue</Th>
                                    <Th right>Cap</Th>
                                    <Th>Agreement</Th>
                                    <Th>Artist terms</Th>
                                    <Th>Venue terms</Th>
                                    <Th right>Actual revenue</Th>
                                    <Th right>Artist</Th>
                                    <Th right>Venue</Th>
                                    <Th>Status</Th>
                                    <Th>—</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.event_id} className="border-b border-[#2a2a2a] hover:bg-[#141414]" data-testid={`settle-row-${r.event_id}`}>
                                        <Td>
                                            <div className="font-display font-bold uppercase truncate max-w-xs">{r.event_name}</div>
                                            <div className="text-[10px] font-mono uppercase text-[#666]">{r.status}</div>
                                        </Td>
                                        <Td className="whitespace-nowrap">{formatDate(r.date)}</Td>
                                        <Td className="truncate max-w-[160px]">{r.venue_name || "TBD"}</Td>
                                        <Td right>{r.capacity ?? "—"}</Td>
                                        <Td>
                                            <span
                                                data-testid={`settle-agreement-${r.event_id}`}
                                                className={`${BADGE_CLS} ${r.agreement_type === "no_agreement" ? "border-[#333] text-[#666]" : "border-[#ff4400] text-[#ff4400]"}`}
                                            >
                                                {AGREEMENT_LABEL[r.agreement_type] || r.agreement_type}
                                            </span>
                                        </Td>
                                        <Td>
                                            <span data-testid={`settle-artist-terms-${r.event_id}`} className="text-xs font-mono">
                                                {r.artist_terms || <span className="text-[#666]">—</span>}
                                            </span>
                                        </Td>
                                        <Td>
                                            <span data-testid={`settle-venue-terms-${r.event_id}`} className="text-xs font-mono">
                                                {r.venue_terms || <span className="text-[#666]">—</span>}
                                            </span>
                                        </Td>
                                        <Td right>
                                            <span data-testid={`settle-actual-${r.event_id}`} className="whitespace-nowrap">
                                                {money(r.actual_revenue, rowCurrency(r)) || <span className="text-[#666]">—</span>}
                                            </span>
                                        </Td>
                                        <Td right>
                                            <span data-testid={`settle-artist-share-${r.event_id}`} className="whitespace-nowrap">
                                                {money(r.artist_share, rowCurrency(r)) || <span className="text-[#666]">—</span>}
                                            </span>
                                        </Td>
                                        <Td right>
                                            <span data-testid={`settle-venue-share-${r.event_id}`} className="whitespace-nowrap">
                                                {money(r.venue_share, rowCurrency(r)) || <span className="text-[#666]">—</span>}
                                            </span>
                                        </Td>
                                        <Td>
                                            <span
                                                data-testid={`settle-status-${r.event_id}`}
                                                className={`${BADGE_CLS} ${STATUS_STYLE[r.settlement_status] || "border-[#333] text-[#666]"}`}
                                            >
                                                {STATUS_LABEL[r.settlement_status] || r.settlement_status}
                                            </span>
                                            {r.settlement_status === "settled" && r.settled_at && (
                                                <div className="mt-1 text-[10px] font-mono uppercase text-[#a3a3a3]" data-testid={`settle-settled-at-${r.event_id}`}>
                                                    {new Date(r.settled_at).toISOString().slice(0, 10)}
                                                </div>
                                            )}
                                        </Td>
                                        <Td>
                                            <div className="flex flex-wrap gap-1">
                                                <Dialog open={editingId === r.event_id} onOpenChange={(o) => setEditingId(o ? r.event_id : null)}>
                                                    <DialogTrigger asChild>
                                                        <button className="pm-btn text-[10px]" data-testid={`settle-edit-${r.event_id}`}>
                                                            <Pencil className="w-3 h-3 mr-1" /> Edit
                                                        </button>
                                                    </DialogTrigger>
                                                    <AgreementDialog row={r} onDone={() => { setEditingId(null); load(); }} />
                                                </Dialog>
                                                {r.settlement_status === "ready_to_settle" && (
                                                    <button
                                                        onClick={() => doSettle(r)}
                                                        disabled={busySettleId === r.event_id}
                                                        className="pm-btn pm-btn-primary text-[10px]"
                                                        data-testid={`settle-mark-settled-${r.event_id}`}
                                                    >
                                                        <Check className="w-3 h-3 mr-1" /> Mark as settled
                                                    </button>
                                                )}
                                                {r.settlement_status === "settled" && (
                                                    <button
                                                        onClick={() => doReopen(r)}
                                                        disabled={busySettleId === r.event_id}
                                                        className="pm-btn text-[10px]"
                                                        data-testid={`settle-reopen-${r.event_id}`}
                                                    >
                                                        <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                                                    </button>
                                                )}
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 text-[10px] font-mono uppercase text-[#666]">
                        Project Mint stores agreement terms per event — it does not process payments or transfer funds.
                    </div>
                </div>
            )}
        </div>
    );
}

function EmptyState() {
    return (
        <div className="px-4 md:px-8 py-16 text-center" data-testid="business-empty">
            <Handshake className="w-8 h-8 text-[#ff4400] mx-auto" />
            <div className="mt-3 font-display font-black uppercase text-2xl">No events yet</div>
            <p className="mt-2 text-sm text-[#a3a3a3] max-w-md mx-auto">
                Create an event, then attach the venue agreement here — flat rental, percentage split,
                guarantee, or none. Project Mint records the terms; it doesn't move money.
            </p>
        </div>
    );
}

function AgreementDialog({ row, onDone }) {
    const initial = row.agreement || {};
    // Currency initialization priority (spec: NEVER default to USD):
    //   1. Explicit stored agreement.currency
    //   2. Event's own currency (row.event_currency)
    //   3. Currency derived from venue country (row.venue_country)
    //   4. "" — require the user to choose
    const suggestedCurrency = (
        initial.currency
        || row.event_currency
        || currencyForCountry(row.venue_country)
        || ""
    );
    const [type, setType] = useState(row.agreement_type || "no_agreement");
    const [artistPct, setArtistPct] = useState(initial.artist_percentage ?? 80);
    const [venuePct, setVenuePct] = useState(initial.venue_percentage ?? 20);
    const [rentalAmount, setRentalAmount] = useState(initial.venue_rental_amount ?? "");
    const [guarantee, setGuarantee] = useState(initial.artist_guarantee_amount ?? "");
    const [currency, setCurrency] = useState(suggestedCurrency);
    const [notes, setNotes] = useState(initial.notes || "");
    const [actualRevenue, setActualRevenue] = useState(row.actual_revenue ?? "");
    const [busy, setBusy] = useState(false);
    const currencyIsSuggestion = !initial.currency && !!suggestedCurrency && currency === suggestedCurrency;
    const currencyRequired = type !== "no_agreement";
    const currencyMissing = currencyRequired && !currency;
    const currencyLabel = currency || "—";

    const submit = async () => {
        if (currencyMissing) {
            toast.error("Choose a currency for this agreement");
            return;
        }
        setBusy(true);
        try {
            const payload = { type, currency: currency || null, notes: notes || null };
            if (type === "percentage_split") {
                payload.artist_percentage = Number(artistPct);
                payload.venue_percentage = Number(venuePct);
            } else if (type === "flat_rental") {
                payload.venue_rental_amount = Number(rentalAmount || 0);
            } else if (type === "artist_guarantee") {
                payload.artist_guarantee_amount = Number(guarantee || 0);
            } else if (type === "guarantee_plus_percentage") {
                payload.artist_guarantee_amount = Number(guarantee || 0);
                payload.artist_percentage = Number(artistPct);
            }
            await endpoints.updateEventAgreement(row.event_id, payload);
            // Actual revenue is a separate write so it's optional and clearable
            const rev = actualRevenue === "" ? null : Number(actualRevenue);
            if ((rev === null && row.actual_revenue != null) || (rev !== null && rev !== row.actual_revenue)) {
                await endpoints.updateEventActualRevenue(row.event_id, { actual_revenue: rev });
            }
            toast.success("Agreement saved");
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-lg" data-testid="agreement-dialog">
            <DialogHeader>
                <DialogTitle className="font-display uppercase text-xl truncate">{row.event_name}</DialogTitle>
                <div className="text-[10px] font-mono uppercase text-[#666]">{row.venue_name || "Venue TBD"} · {formatDate(row.date)}</div>
            </DialogHeader>
            <div className="space-y-4">
                <label className="block">
                    <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">Agreement type</span>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 font-mono uppercase text-xs outline-none"
                        data-testid="agreement-type"
                    >
                        {Object.entries(AGREEMENT_LABEL).map(([v, l]) => (
                            <option key={v} value={v} className="bg-[#141414]">{l}</option>
                        ))}
                    </select>
                </label>

                {currencyRequired && (
                    <div>
                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mb-1">Agreement currency</div>
                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs outline-none focus:border-[#ff4400]"
                            data-testid="agreement-currency-select"
                        >
                            <option value="" className="bg-[#141414]">Select currency…</option>
                            {SUPPORTED_CURRENCIES.map((c) => (
                                <option key={c.code} value={c.code} className="bg-[#141414]">
                                    {c.code} — {c.label}
                                </option>
                            ))}
                        </select>
                        <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase" data-testid="agreement-currency-hint">
                            {currencyMissing ? (
                                <span className="text-[#ffea00]">Currency required · Project Mint will not assume USD</span>
                            ) : currencyIsSuggestion ? (
                                <span className="text-[#a3a3a3]">
                                    Suggested from {initial.currency ? "agreement" : (row.event_currency ? "event" : `venue country${row.venue_country ? ` (${row.venue_country})` : ""}`)}
                                </span>
                            ) : (
                                <span className="text-[#ff4400]">Manually set</span>
                            )}
                        </div>
                    </div>
                )}

                {type === "percentage_split" && (
                    <div className="grid grid-cols-2 gap-3" data-testid="agreement-percentage-fields">
                        <NumField label="Artist %" value={artistPct} onChange={(v) => { setArtistPct(v); setVenuePct(Math.max(0, 100 - Number(v || 0))); }} testId="agreement-artist-pct" />
                        <NumField label="Venue %" value={venuePct} onChange={(v) => { setVenuePct(v); setArtistPct(Math.max(0, 100 - Number(v || 0))); }} testId="agreement-venue-pct" />
                    </div>
                )}
                {type === "flat_rental" && (
                    <NumField label={`Venue rental (${currencyLabel})`} value={rentalAmount} onChange={setRentalAmount} testId="agreement-rental" />
                )}
                {type === "artist_guarantee" && (
                    <NumField label={`Artist guarantee (${currencyLabel})`} value={guarantee} onChange={setGuarantee} testId="agreement-guarantee" />
                )}
                {type === "guarantee_plus_percentage" && (
                    <div className="grid grid-cols-2 gap-3">
                        <NumField label={`Artist guarantee (${currencyLabel})`} value={guarantee} onChange={setGuarantee} testId="agreement-guarantee" />
                        <NumField label="Artist % of overage" value={artistPct} onChange={setArtistPct} testId="agreement-overage-pct" />
                    </div>
                )}
                {type === "no_agreement" && (
                    <div className="text-[10px] font-mono uppercase text-[#666] border border-[#2a2a2a] p-3" data-testid="agreement-no-agreement-hint">
                        No settlement will be calculated. Use this when the event has no revenue-sharing terms.
                    </div>
                )}

                <label className="block">
                    <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                        Actual revenue (post-event, optional){currency ? ` — ${currency}` : ""}
                    </span>
                    <input
                        type="number" step="0.01" min="0"
                        value={actualRevenue}
                        placeholder="0"
                        onChange={(e) => setActualRevenue(e.target.value)}
                        onBlur={(e) => {
                            const v = e.target.value;
                            if (v === "" || v == null) return;
                            const num = Number(v);
                            if (Number.isNaN(num)) return;
                            setActualRevenue(String(num));
                        }}
                        className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono"
                        data-testid="agreement-actual-revenue"
                    />
                </label>

                <label className="block">
                    <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">Notes (optional)</span>
                    <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full mt-1 bg-transparent border border-[#2a2a2a] py-2 px-2 outline-none font-mono text-xs" data-testid="agreement-notes" />
                </label>
            </div>
            <DialogFooter>
                <button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="agreement-save">
                    Save agreement
                </button>
            </DialogFooter>
        </DialogContent>
    );
}

function NumField({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="text-[10px] font-mono uppercase text-[#a3a3a3]">{label}</span>
            <input
                type="number" step="0.01" min="0"
                value={value ?? ""}
                placeholder="0"
                onChange={(e) => onChange(e.target.value)}
                onBlur={(e) => {
                    // Same leading-zero guard as EventEditor ticket price —
                    // strip "029000" on commit while preserving "0" and "0.50".
                    const v = e.target.value;
                    if (v === "" || v == null) return;
                    const num = Number(v);
                    if (Number.isNaN(num)) return;
                    onChange(String(num));
                }}
                className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono"
                data-testid={testId}
            />
        </label>
    );
}

function Th({ children, right }) {
    return <th className={`px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-[#666] ${right ? "text-right" : "text-left"}`}>{children}</th>;
}
function Td({ children, right, className = "" }) {
    return <td className={`px-3 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>;
}
